import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  X,
  Users,
  Trophy,
  Shuffle,
  ChevronRight,
  GitBranch,
  LayoutGrid,
  Swords,
  Image,
  Upload,
  Medal,
  BarChart3,
} from "lucide-react";
import { useTournament } from "../context/TournamentContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import LanguageSelector from "../components/LanguageSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const SPORTS = [
  { value: "football", label: "Football" },
  { value: "basketball", label: "Basketball" },
  { value: "badminton", label: "Badminton" },
  { value: "ping-pong", label: "Ping Pong" },
  { value: "tennis", label: "Tennis" },
  { value: "volleyball", label: "Volleyball" },
  { value: "esports", label: "Esports" },
  { value: "board-games", label: "Board Games" },
  { value: "custom", label: "Custom Sport" },
];

export default function SetupPage() {
  const navigate = useNavigate();
  const { createTournament, editTournament, loadTournament } = useTournament();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditMode = !!editId;

  // Step 1: Basic Info
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [sportType, setSportType] = useState("");
  const [customSport, setCustomSport] = useState("");
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Step 2: Format
  const [format, setFormat] = useState(""); // 'league' or 'bracket'
  const [hasLosersLeague, setHasLosersLeague] = useState(false);
  const [hasBronzeMatch, setHasBronzeMatch] = useState(false);
  const [hasPlayerStats, setHasPlayerStats] = useState(false);

  // Step 3: Participants (now with optional logo)
  const [participants, setParticipants] = useState([]); // [{name: string, logo: string|null}]
  const [newParticipant, setNewParticipant] = useState("");
  
  // Logo editing for existing participants
  const [editingLogoIndex, setEditingLogoIndex] = useState(null);
  const participantLogoInputRef = useRef(null);

  // Current step
  const [step, setStep] = useState(1);

  // Edit mode bookkeeping
  const [originalSnapshot, setOriginalSnapshot] = useState(null);
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(isEditMode);

  // Pre-fill the form when editing an existing tournament
  useEffect(() => {
    if (!editId) return;
    let active = true;
    setIsPrefilling(true);
    (async () => {
      const tour = await loadTournament(editId);
      if (!active) return;
      if (!tour) { setIsPrefilling(false); return; }
      setName(tour.name || "");
      setCode(tour.code || "");
      const knownSport = SPORTS.some((s) => s.value === tour.sportType && s.value !== "custom");
      if (knownSport) {
        setSportType(tour.sportType);
      } else {
        setSportType("custom");
        setCustomSport(tour.sportType || "");
      }
      setFormat(tour.format || "");
      setHasLosersLeague(!!tour.hasLosersLeague);
      setHasBronzeMatch(!!tour.hasBronzeMatch);
      setHasPlayerStats(!!tour.hasPlayerStats);
      const prefilled = (tour.participants || []).map((p) => ({
        id: p.id,
        name: p.name,
        logo: p.logo ?? null,
        players: Array.isArray(p.players) ? p.players : [],
      }));
      setParticipants(prefilled);
      setOriginalSnapshot({
        ids: prefilled.map((p) => p.id),
        format: tour.format || "",
        hasLosersLeague: !!tour.hasLosersLeague,
        hasBronzeMatch: !!tour.hasBronzeMatch,
      });
      setIsPrefilling(false);
    })();
    return () => { active = false; };
  }, [editId, loadTournament]);

  const validParticipants = participants.filter((p) => p.name?.trim() !== "");
  const finalSport = sportType === "custom" ? customSport : sportType;
  const canProceedStep1 = name.trim() && code.trim().length >= 4 && finalSport;

  // Check for duplicate name/code before proceeding to Step 2
  const handleStep1Next = async () => {
    if (!canProceedStep1) return;

    // In edit mode skip the global duplicate pre-check (it would flag the
    // tournament's own name); the backend validates uniqueness excluding self.
    if (isEditMode) {
      setStep(2);
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const response = await fetch(`${API_URL}/api/tournaments/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), code: code.trim().toUpperCase() }),
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.nameExists && result.codeExists) {
          toast.error(t('toast.bothExist'));
          return;
        } else if (result.nameExists) {
          toast.error(t('toast.nameExists'));
          return;
        } else if (result.codeExists) {
          toast.error(t('toast.codeExists'));
          return;
        }
      }
      
      // No duplicates, proceed to Step 2
      setStep(2);
    } catch (error) {
      console.error('Error checking duplicates:', error);
      // If check fails, still allow proceeding (backend will catch duplicates)
      setStep(2);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const addParticipant = () => {
    if (newParticipant.trim()) {
      setParticipants([...participants, { name: newParticipant.trim(), logo: null }]);
      setNewParticipant("");
    }
  };

  // Handle logo upload for a specific participant
  const handleParticipantLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file && editingLogoIndex !== null) {
      if (file.size > 500000) { // 500KB limit
        toast.error(t('toast.logoTooLarge'));
        return;
      }
      try {
        const base64 = await fileToBase64(file);
        const updatedParticipants = [...participants];
        updatedParticipants[editingLogoIndex] = {
          ...updatedParticipants[editingLogoIndex],
          logo: base64
        };
        setParticipants(updatedParticipants);
        toast.success(t('toast.logoAdded'));
      } catch {
        toast.error(t('toast.logoFailed'));
      }
    }
    setEditingLogoIndex(null);
    if (participantLogoInputRef.current) participantLogoInputRef.current.value = "";
  };

  // Click on number/logo to change logo
  const handleLogoClick = (index) => {
    setEditingLogoIndex(index);
    setTimeout(() => {
      participantLogoInputRef.current?.click();
    }, 0);
  };

  // Remove logo from participant
  const removeParticipantLogo = (index, e) => {
    e.stopPropagation();
    const updatedParticipants = [...participants];
    updatedParticipants[index] = {
      ...updatedParticipants[index],
      logo: null
    };
    setParticipants(updatedParticipants);
    toast.success(t('toast.logoRemoved'));
  };

  const removeParticipant = (index) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const updateParticipantName = (index, value) => {
    const updated = [...participants];
    updated[index] = { ...updated[index], name: value };
    setParticipants(updated);
  };

  const shuffleParticipants = () => {
    const shuffled = [...validParticipants].sort(() => Math.random() - 0.5);
    setParticipants(shuffled);
    toast.success(t('toast.shuffled'));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addParticipant();
    }
  };

  const canProceedStep2 = format !== "";
  const canProceedStep3 = validParticipants.length >= 2;

  const handleNext = async () => {
    if (step === 1 && canProceedStep1) {
      await handleStep1Next();
    } else if (step === 2 && canProceedStep2) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else navigate("/");
  };
  // Player management (only used when Player Statistics is enabled)
  const genPlayerId = () => `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const addPlayer = (teamIndex) => {
    const updated = [...participants];
    const team = { ...updated[teamIndex] };
    team.players = [...(team.players || []), { id: genPlayerId(), name: "" }];
    updated[teamIndex] = team;
    setParticipants(updated);
  };
  const updatePlayerName = (teamIndex, playerIndex, value) => {
    const updated = [...participants];
    const team = { ...updated[teamIndex] };
    const players = [...(team.players || [])];
    players[playerIndex] = { ...players[playerIndex], name: value };
    team.players = players;
    updated[teamIndex] = team;
    setParticipants(updated);
  };
  const removePlayer = (teamIndex, playerIndex) => {
    const updated = [...participants];
    const team = { ...updated[teamIndex] };
    team.players = (team.players || []).filter((_, i) => i !== playerIndex);
    updated[teamIndex] = team;
    setParticipants(updated);
  };



  const buildConfig = () => ({
    name,
    code: code.toUpperCase(),
    sportType: finalSport,
    format,
    participants: validParticipants,
    hasLosersLeague: format === "bracket" && hasLosersLeague,
    hasBronzeMatch: format === "bracket" && hasBronzeMatch,
    hasPlayerStats,
  });

  // Determine whether the edit requires a destructive rebuild
  const isStructuralChange = () => {
    if (!originalSnapshot) return true;
    if (format !== originalSnapshot.format) return true;
    if ((format === "bracket" && hasLosersLeague) !== originalSnapshot.hasLosersLeague && format === "bracket") return true;
    if ((format === "bracket" && hasBronzeMatch) !== originalSnapshot.hasBronzeMatch && format === "bracket") return true;
    const ids = validParticipants.map((p) => p.id);
    if (ids.length !== originalSnapshot.ids.length) return true;
    for (let i = 0; i < ids.length; i++) {
      if (ids[i] !== originalSnapshot.ids[i]) return true;
    }
    return false;
  };

  const runEdit = async (rebuild) => {
    setIsSaving(true);
    try {
      toast.loading(t('loading'));
      await editTournament(editId, buildConfig(), rebuild);
      toast.dismiss();
      toast.success(t('toast.tournamentUpdated'));
      if (format === "league") navigate(`/league/${editId}`);
      else navigate(`/bracket/${editId}`);
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || t('toast.createFailed'));
    } finally {
      setIsSaving(false);
      setShowRebuildConfirm(false);
    }
  };

  const handleSubmit = async () => {
    if (!canProceedStep3) {
      toast.error(t('toast.addAtLeast2'));
      return;
    }

    // Bronze medal match requires at least 4 players (need 2 semifinal losers)
    if (hasBronzeMatch && validParticipants.length < 4) {
      toast.error(t('toast.bronzeNeeds4'));
      return;
    }

    if (isEditMode) {
      if (isStructuralChange()) {
        setShowRebuildConfirm(true);
      } else {
        await runEdit(false);
      }
      return;
    }

    try {
      toast.loading(t('loading'));
      const tournament = await createTournament({
        name,
        code: code.toUpperCase(),
        sportType: finalSport,
        format,
        participants: validParticipants,
        hasLosersLeague: format === "bracket" && hasLosersLeague,
        hasBronzeMatch: format === "bracket" && hasBronzeMatch,
        hasPlayerStats,
      });
      toast.dismiss();
      toast.success(t('toast.tournamentCreated'));

      if (format === "league") {
        navigate(`/league/${tournament.id}`);
      } else {
        navigate(`/bracket/${tournament.id}`);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || t('toast.createFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-neon-cyan/20 rounded flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-neon-cyan" />
                </div>
                <h1 className="font-heading text-xl font-bold tracking-tight text-white uppercase">
                  {isEditMode ? t('setup.editTournament') : t('home.createNew')}
                </h1>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="container mx-auto px-6 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${
                    step >= s
                      ? "bg-neon-cyan text-black"
                      : "bg-white/10 text-white/40"
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-24 md:w-32 h-1 mx-2 ${
                      step > s ? "bg-neon-cyan" : "bg-white/10"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Loading state while pre-filling in edit mode */}
          {isEditMode && isPrefilling && (
            <div className="card-surface p-10 flex items-center justify-center" data-testid="edit-loading">
              <span className="text-white/60 font-data uppercase tracking-widest text-sm animate-pulse">
                {t('loading')}
              </span>
            </div>
          )}

          {/* Step 1: Basic Info */}
          {step === 1 && !(isEditMode && isPrefilling) && (
            <div className="card-surface p-6 space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-neon-cyan" />
                <h2 className="font-heading text-lg uppercase tracking-wide text-white">
                  {t('setup.step1')}
                </h2>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-white/60 text-sm uppercase tracking-wide">
                    {t('setup.tournamentName')}
                  </Label>
                  <Input
                    id="name"
                    data-testid="tournament-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Grade 8 Volleyball – Period 3"
                    className="input-dark mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="code" className="text-white/60 text-sm uppercase tracking-wide">
                    {t('setup.tournamentCode')}
                  </Label>
                  <Input
                    id="code"
                    data-testid="tournament-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="e.g., ABC123"
                    className="input-dark mt-2 uppercase"
                    maxLength={10}
                  />
                  <p className="text-white/40 text-xs mt-1">{t('setup.codeHint')}</p>
                  {code.trim().length > 0 && code.trim().length < 4 && (
                    <p className="text-red-400 text-xs mt-1" data-testid="code-min-length-hint">{t('setup.codeMinLength')}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="sport" className="text-white/60 text-sm uppercase tracking-wide">
                    {t('setup.selectSport')}
                  </Label>
                  <Select value={sportType} onValueChange={setSportType}>
                    <SelectTrigger
                      id="sport"
                      data-testid="sport-type-select"
                      className="input-dark mt-2"
                    >
                      <SelectValue placeholder={t('setup.selectSport')} />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-card border-white/10">
                      {SPORTS.map((sport) => (
                        <SelectItem
                          key={sport.value}
                          value={sport.value}
                          className="text-white hover:bg-white/10 focus:bg-white/10"
                          data-testid={`sport-option-${sport.value}`}
                        >
                          {t(`sports.${sport.value.replace('-', '')}`) || sport.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {sportType === "custom" && (
                  <div>
                    <Label htmlFor="customSport" className="text-white/60 text-sm uppercase tracking-wide">
                      {t('setup.customSport')}
                    </Label>
                    <Input
                      id="customSport"
                      data-testid="custom-sport-input"
                      value={customSport}
                      onChange={(e) => setCustomSport(e.target.value)}
                      placeholder="e.g., Ping Pong, Darts, etc."
                      className="input-dark mt-2"
                    />
                  </div>
                )}
              </div>

              <Button
                data-testid="next-step-1-btn"
                onClick={handleNext}
                disabled={!canProceedStep1 || isCheckingDuplicate}
                className="w-full btn-skewed bg-neon-cyan hover:bg-neon-cyan/90 text-black font-heading font-bold uppercase tracking-widest py-6 disabled:opacity-50"
              >
                <span className="flex items-center justify-center gap-2">
                  {isCheckingDuplicate ? t('loading') : t('setup.chooseFormat')}
                  <ChevronRight className="w-5 h-5" />
                </span>
              </Button>
            </div>
          )}

          {/* Step 2: Format Selection */}
          {step === 2 && (
            <div className="card-surface p-6 space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <GitBranch className="w-5 h-5 text-neon-cyan" />
                <h2 className="font-heading text-lg uppercase tracking-wide text-white">
                  {t('setup.step2')}
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* League Option */}
                <button
                  data-testid="format-league-btn"
                  onClick={() => setFormat("league")}
                  className={`p-6 border-2 rounded transition-all duration-200 text-left ${
                    format === "league"
                      ? "border-neon-cyan bg-neon-cyan/10"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <LayoutGrid
                    className={`w-8 h-8 mb-3 ${
                      format === "league" ? "text-neon-cyan" : "text-white/60"
                    }`}
                  />
                  <h3 className="font-heading text-lg uppercase text-white mb-1">{t('setup.league')}</h3>
                  <p className="text-white/40 text-sm">
                    {t('setup.leagueDesc')}
                  </p>
                </button>

                {/* Bracket Option */}
                <button
                  data-testid="format-bracket-btn"
                  onClick={() => setFormat("bracket")}
                  className={`p-6 border-2 rounded transition-all duration-200 text-left ${
                    format === "bracket"
                      ? "border-neon-cyan bg-neon-cyan/10"
                      : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <GitBranch
                    className={`w-8 h-8 mb-3 ${
                      format === "bracket" ? "text-neon-cyan" : "text-white/60"
                    }`}
                  />
                  <h3 className="font-heading text-lg uppercase text-white mb-1">{t('setup.bracket')}</h3>
                  <p className="text-white/40 text-sm">
                    {t('setup.bracketDesc')}
                  </p>
                </button>
              </div>

              {/* Bracket Options - Mutually Exclusive */}
              {format === "bracket" && (
                <div className="space-y-3">
                  <p className="text-white/40 text-xs uppercase tracking-wide">{t('setup.optionalFeatures')}</p>
                  
                  {/* Double Elimination Option */}
                  <div 
                    className={`border p-4 rounded cursor-pointer transition-all ${
                      hasLosersLeague 
                        ? "bg-neon-purple/10 border-neon-purple" 
                        : "bg-white/5 border-white/10 hover:border-white/30"
                    }`}
                    onClick={() => {
                      if (!hasLosersLeague) {
                        setHasLosersLeague(true);
                        setHasBronzeMatch(false);
                      } else {
                        setHasLosersLeague(false);
                      }
                    }}
                    data-testid="double-elimination-option"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Swords className={`w-5 h-5 ${hasLosersLeague ? "text-neon-purple" : "text-white/60"}`} />
                        <div>
                          <h4 className={`font-heading uppercase ${hasLosersLeague ? "text-neon-purple" : "text-white"}`}>
                            {t('setup.doubleElimination')}
                          </h4>
                          <p className="text-white/40 text-sm">
                            {t('setup.doubleElimDesc')}
                          </p>
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        hasLosersLeague ? "border-neon-purple bg-neon-purple" : "border-white/30"
                      }`}>
                        {hasLosersLeague && <div className="w-2 h-2 bg-black rounded-full" />}
                      </div>
                    </div>
                  </div>

                  {/* Bronze Medal Match Option */}
                  <div 
                    className={`border p-4 rounded cursor-pointer transition-all ${
                      hasBronzeMatch 
                        ? "bg-orange-500/10 border-orange-500" 
                        : "bg-white/5 border-white/10 hover:border-white/30"
                    }`}
                    onClick={() => {
                      if (!hasBronzeMatch) {
                        setHasBronzeMatch(true);
                        setHasLosersLeague(false);
                      } else {
                        setHasBronzeMatch(false);
                      }
                    }}
                    data-testid="bronze-match-option"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Medal className={`w-5 h-5 ${hasBronzeMatch ? "text-orange-500" : "text-white/60"}`} />
                        <div>
                          <h4 className={`font-heading uppercase ${hasBronzeMatch ? "text-orange-500" : "text-white"}`}>
                            {t('setup.bronzeMedal')}
                          </h4>
                          <p className="text-white/40 text-sm">
                            {t('setup.bronzeMedalDesc')}
                          </p>
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        hasBronzeMatch ? "border-orange-500 bg-orange-500" : "border-white/30"
                      }`}>
                        {hasBronzeMatch && <div className="w-2 h-2 bg-black rounded-full" />}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Player Statistics toggle (works for any format) */}
              {format && (
                <div
                  data-testid="player-stats-toggle"
                  onClick={() => setHasPlayerStats((v) => !v)}
                  className={`p-4 border-2 rounded cursor-pointer transition-all duration-200 ${
                    hasPlayerStats ? "border-neon-cyan bg-neon-cyan/10" : "border-white/20 hover:border-white/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <BarChart3 className={`w-5 h-5 ${hasPlayerStats ? "text-neon-cyan" : "text-white/60"}`} />
                      <div>
                        <h4 className={`font-heading uppercase ${hasPlayerStats ? "text-neon-cyan" : "text-white"}`}>
                          {t('setup.enablePlayerStats')}
                        </h4>
                        <p className="text-white/40 text-sm">{t('setup.enablePlayerStatsDesc')}</p>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      hasPlayerStats ? "border-neon-cyan bg-neon-cyan" : "border-white/30"
                    }`}>
                      {hasPlayerStats && <div className="w-2 h-2 bg-black rounded-full" />}
                    </div>
                  </div>
                </div>
              )}

              <Button
                data-testid="next-step-2-btn"
                onClick={handleNext}
                disabled={!canProceedStep2}
                className="w-full btn-skewed bg-neon-cyan hover:bg-neon-cyan/90 text-black font-heading font-bold uppercase tracking-widest py-6 disabled:opacity-50"
              >
                <span className="flex items-center justify-center gap-2">
                  {t('setup.nextAddParticipants')}
                  <ChevronRight className="w-5 h-5" />
                </span>
              </Button>
            </div>
          )}

          {/* Step 3: Participants */}
          {step === 3 && (
            <div className="card-surface p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-neon-cyan" />
                  <h2 className="font-heading text-base sm:text-lg uppercase tracking-wide text-white">
                    {format === "league" ? t('setup.step3TitleLeague') : t('setup.step3Title')}
                  </h2>
                  <span className="text-white/40 text-sm font-data ml-2">
                    ({validParticipants.length})
                  </span>
                </div>
                {validParticipants.length >= 2 && (
                  <Button
                    type="button"
                    data-testid="shuffle-participants-btn"
                    variant="ghost"
                    size="sm"
                    onClick={shuffleParticipants}
                    className="text-white/60 hover:text-neon-cyan"
                  >
                    <Shuffle className="w-4 h-4 mr-2" />
                    {t('setup.shuffle')}
                  </Button>
                )}
              </div>

              {/* Add participant input */}
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    data-testid="new-participant-input"
                    value={newParticipant}
                    onChange={(e) => setNewParticipant(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={format === "league" ? t('setup.enterTeam') : t('setup.enterParticipant')}
                    className="input-dark flex-1"
                  />
                  <Button
                    type="button"
                    data-testid="add-participant-btn"
                    onClick={addParticipant}
                    className="bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan/30 border border-neon-cyan/30"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
                <p className="text-white/40 text-xs">
                  {t('setup.clickToAddLogo')}
                </p>
              </div>

              {/* Hidden file input for participant logos */}
              <input
                ref={participantLogoInputRef}
                type="file"
                accept="image/*"
                onChange={handleParticipantLogoUpload}
                className="hidden"
              />

              {/* Participant list */}
              <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-hide">
                {participants.map((participant, index) => (
                  <div
                    key={participant.id || index}
                    className="bg-white/5 border border-white/10 p-3 rounded group"
                    data-testid={`participant-${index}`}
                  >
                   <div className="flex items-center gap-3">
                    {/* Clickable logo/number box */}
                    <div 
                      onClick={() => handleLogoClick(index)}
                      className="relative cursor-pointer group/logo"
                      title={participant.logo ? t('setup.clickToAddLogo') : t('setup.clickToAddLogo')}
                    >
                      {participant.logo ? (
                        <div className="relative">
                          <img 
                            src={participant.logo} 
                            alt="" 
                            className="w-8 h-8 rounded object-cover ring-2 ring-neon-cyan/50 hover:ring-neon-cyan transition-all" 
                          />
                          <button
                            onClick={(e) => removeParticipantLogo(index, e)}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover/logo:opacity-100 transition-opacity"
                          >
                            <X className="w-2.5 h-2.5 text-white" />
                          </button>
                        </div>
                      ) : (
                        <span className="w-8 h-8 bg-white/10 rounded flex items-center justify-center text-white/60 font-data text-sm hover:bg-neon-cyan/20 hover:text-neon-cyan transition-colors border border-transparent hover:border-neon-cyan/30">
                          {index + 1}
                        </span>
                      )}
                    </div>
                    <Input
                      data-testid={`participant-${index}-name-input`}
                      value={participant.name}
                      onChange={(e) => updateParticipantName(index, e.target.value)}
                      className="flex-1 input-dark font-data h-9"
                    />
                    <Button
                      type="button"
                      data-testid={`remove-participant-${index}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => removeParticipant(index)}
                      className="opacity-100 sm:opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-500 hover:bg-red-500/10 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                   </div>

                   {/* Players sub-list (Player Statistics enabled) */}
                   {hasPlayerStats && (
                     <div className="mt-3 pl-11 space-y-2" data-testid={`participant-${index}-players`}>
                       {(participant.players || []).map((pl, pi) => (
                         <div key={pl.id || pi} className="flex items-center gap-2">
                           <span className="text-white/30 text-xs font-data w-4">{pi + 1}</span>
                           <Input
                             data-testid={`participant-${index}-player-${pi}-input`}
                             value={pl.name}
                             onChange={(e) => updatePlayerName(index, pi, e.target.value)}
                             placeholder={t('setup.playerName')}
                             className="flex-1 input-dark font-data h-8 text-sm"
                           />
                           <Button
                             type="button"
                             data-testid={`participant-${index}-remove-player-${pi}`}
                             variant="ghost"
                             size="icon"
                             onClick={() => removePlayer(index, pi)}
                             className="h-8 w-8 text-white/40 hover:text-red-500 hover:bg-red-500/10"
                           >
                             <X className="w-3.5 h-3.5" />
                           </Button>
                         </div>
                       ))}
                       <button
                         type="button"
                         data-testid={`participant-${index}-add-player`}
                         onClick={() => addPlayer(index)}
                         className="flex items-center gap-1 text-neon-cyan/80 hover:text-neon-cyan text-xs uppercase tracking-wide"
                       >
                         <Plus className="w-3.5 h-3.5" /> {t('setup.addPlayer')}
                       </button>
                     </div>
                   )}
                  </div>
                ))}
              </div>

              {validParticipants.length < 2 && (
                <p className="text-white/40 text-sm text-center py-4 border border-dashed border-white/10 rounded">
                  {t('setup.addAtLeast')} {format === "league" ? t('home.teams') : t('setup.participants')}
                </p>
              )}

              <Button
                data-testid="generate-tournament-btn"
                onClick={handleSubmit}
                disabled={!canProceedStep3 || isSaving}
                className="w-full btn-skewed bg-neon-green hover:bg-neon-green/90 text-black font-heading font-bold uppercase tracking-widest py-6 disabled:opacity-50"
              >
                <span className="flex items-center justify-center gap-2">
                  <Trophy className="w-5 h-5" />
                  {isEditMode
                    ? t('setup.saveChanges')
                    : format === "league" ? t('setup.generateLeague') : t('setup.generateBracket')}
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showRebuildConfirm} onOpenChange={setShowRebuildConfirm}>
        <AlertDialogContent className="bg-surface-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase text-white">
              {t('setup.rebuildTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {t('setup.rebuildWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-rebuild-btn"
              onClick={() => runEdit(true)}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {t('setup.rebuildConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
